<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_strategy_title')"
      :subtitle="$t('ga_admin_strategy_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('ga_admin_loading_strategy') }}</div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>
        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_analysis_section_feedback') }}</div>
              <div class="section-desc">{{ $t('ga_admin_analysis_section_feedback_desc') }}</div>
            </div>
          </div>
          <div class="feedback-analysis-grid">
            <div v-for="item in feedbackSummaryAnalyses" :key="item.key" class="feedback-analysis-card">
              <div class="analysis-top">
                <div>
                  <div class="analysis-title">{{ item.title }}</div>
                  <div class="analysis-subtitle">{{ item.subtitle }}</div>
                </div>
                <button class="ghost-btn ai-action-btn compact" @click="openAi(item.prompt)">
                  <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
                  <span>{{ $t('ga_admin_analysis_deep_dive') }}</span>
                </button>
              </div>
              <div class="analysis-summary">{{ item.summary }}</div>
              <div class="analysis-metrics">
                <div v-for="metric in item.metrics" :key="metric.label" class="analysis-metric">
                  <span>{{ metric.label }}</span>
                  <strong>{{ metric.value }}</strong>
                </div>
              </div>
              <div v-if="item.insights && item.insights.length" class="insight-list">
                <div v-for="insight in item.insights" :key="insight.title" class="insight-item">
                  <div class="insight-top">
                    <span>{{ insight.title }}</span>
                    <em>{{ insight.count }}</em>
                  </div>
                  <div class="insight-desc">{{ insight.desc }}</div>
                  <div v-if="insight.answerDistribution && insight.answerDistribution.length" class="answer-distribution">
                    <span class="answer-label">{{ $t('ga_admin_main_answers') }}</span>
                    <span v-for="answer in insight.answerDistribution" :key="answer.answer" class="answer-chip">
                      <span class="answer-text">{{ answer.answer }}</span>
                      <span class="answer-count">{{ $t('ga_admin_count_feedback', { count: answer.count }) }}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="item.tags && item.tags.length" class="tag-list">
                <span v-for="tag in item.tags" :key="tag" class="tag-chip">{{ tag }}</span>
              </div>
              <div v-if="item.examples && item.examples.length" class="example-list">
                <div v-for="example in item.examples" :key="example.question + example.text" class="example-item">
                  <div class="example-question">{{ example.question }}</div>
                  <div class="example-text">{{ example.text }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_strategy_section_ai') }}</div>
              <div class="section-desc">{{ $t('ga_admin_strategy_section_ai_desc') }}</div>
            </div>
          </div>
          <div class="recommendation-grid">
            <div v-for="item in strategyRecommendations" :key="item.title" class="recommendation-card">
              <div class="card-top">
                <div class="recommendation-title">{{ item.title }}</div>
                <span class="level-pill">{{ item.level }}</span>
              </div>
              <div class="recommendation-desc">{{ item.desc }}</div>
              <div class="action-list">
                <div v-for="action in item.actions" :key="action" class="action-chip">{{ action }}</div>
              </div>
              <button class="ghost-btn ai-action-btn wide" @click="openAi(item.prompt)">
                <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
                <span>{{ $t('ga_admin_strategy_view_details') }}</span>
              </button>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_strategy_section_report_templates') }}</div>
              <div class="section-desc">{{ $t('ga_admin_strategy_section_report_templates_desc') }}</div>
            </div>
          </div>
          <div class="prompt-list">
            <button v-for="item in reportPrompts" :key="item" class="prompt-btn ai-prompt-btn" @click="openAi(item)">
              <span class="prompt-ai-row">
                <span class="prompt-icon-wrap">
                  <font-awesome-icon :icon="['far', 'lightbulb']" class="prompt-icon" />
                </span>
                <span class="prompt-hint">{{ $t('ga_admin_prompt_send_to_ai') }}</span>
              </span>
              <span class="prompt-text">{{ item }}</span>
            </button>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';

export default {
  name: 'GAAdminStrategy',
  components: {
    AdminHeader,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      analytics: {
        strategyRecommendations: [],
        feedbackSummaryAnalyses: [],
        reportPrompts: [],
      },
    };
  },
  computed: {
    strategyRecommendations() {
      return this.analytics.strategyRecommendations;
    },
    feedbackSummaryAnalyses() {
      return this.analytics.feedbackSummaryAnalyses;
    },
    reportPrompts() {
      return this.analytics.reportPrompts;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const analytics = await loadGaAdminAnalytics();
        this.analytics = {
          ...this.analytics,
          ...analytics,
        };
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_strategy');
      } finally {
        this.loading = false;
      }
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #f2f5f9;
  background: linear-gradient(175deg, #e6f0ff 10%, #f0e9f4 30%, #f2f5f9 50%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #aa341b;
}

.loading-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 16px;
  background: #fff;
  color: #666;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #f3d8d2;
  border-top-color: #aa341b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-card,
.feedback-analysis-card,
.recommendation-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.recommendation-grid,
.feedback-analysis-grid,
.prompt-list {
  display: grid;
  gap: 12px;
}

.feedback-analysis-card,
.recommendation-card {
  padding: 14px;
}

.card-top,
.analysis-top,
.insight-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.analysis-top {
  flex-wrap: wrap;
}

.analysis-title,
.recommendation-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.analysis-subtitle {
  margin-top: 4px;
  font-size: 11px;
  color: #999;
}

.analysis-summary {
  margin-top: 12px;
  font-size: 13px;
  line-height: 1.7;
  color: #555;
}

.analysis-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.analysis-metric {
  min-height: 58px;
  padding: 9px 10px;
  border-radius: 12px;
  background: #f7f9fc;
}

.analysis-metric span {
  display: block;
  font-size: 11px;
  color: #8a8f99;
}

.analysis-metric strong {
  display: block;
  margin-top: 6px;
  font-size: 17px;
  color: #333;
}

.insight-list,
.example-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.insight-item {
  padding: 10px;
  border-radius: 12px;
  background: #fff8f5;
}

.insight-top span {
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: #333;
}

.insight-top em {
  flex-shrink: 0;
  font-style: normal;
  font-size: 11px;
  color: #aa341b;
}

.insight-desc,
.example-text {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.answer-distribution {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.answer-label {
  color: #777;
}

.answer-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #fff;
}

.answer-text {
  color: #333;
  font-weight: 600;
}

.answer-count {
  color: #aa341b;
  font-weight: 700;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 12px;
}

.tag-chip {
  padding: 5px 9px;
  border-radius: 999px;
  background: #edf5ff;
  color: #386ca8;
  font-size: 11px;
}

.example-item {
  padding: 10px;
  border-left: 3px solid #f0b4a8;
  border-radius: 10px;
  background: #fafafa;
}

.example-question {
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  color: #777;
}

.level-pill {
  flex-shrink: 0;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
}

.level-pill {
  background: #fff1e8;
  color: #aa341b;
}

.recommendation-desc {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.action-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.action-chip {
  padding: 6px 10px;
  border-radius: 999px;
  background: #fceceb;
  color: #aa341b;
  font-size: 11px;
}

.ghost-btn {
  border: 1.5px solid #aa341b;
  border-radius: 999px;
  padding: 8px 12px;
  background: #fff;
  color: #aa341b;
  font-size: 12px;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #4d50e9 0%, #b84fd7 48%, #aa341b 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(170, 52, 27, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

.ghost-btn.wide {
  width: 100%;
  margin-top: 12px;
}

.ghost-btn.compact {
  padding: 7px 10px;
  white-space: nowrap;
}

.prompt-btn {
  width: 100%;
  border: 1px solid #eadfdd;
  border-radius: 14px;
  padding: 12px 14px;
  text-align: left;
  background: #fff;
  color: #333;
  font-size: 13px;
  line-height: 1.5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ai-prompt-btn {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.prompt-ai-row {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.prompt-icon-wrap {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-top: 1px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4d50e9 0%, #b84fd7 48%, #aa341b 100%);
  color: #fff;
  box-shadow: 0 4px 10px rgba(77, 80, 233, 0.18);
}

.prompt-icon {
  font-size: 14px;
}

.prompt-content,
.prompt-text,
.prompt-hint {
  display: block;
  min-width: 0;
}

.prompt-text {
  padding-left: 2px;
  font-weight: 600;
  color: #333;
}

.prompt-hint {
  color: #aa341b;
  font-size: 11px;
  font-weight: 700;
}
</style>
